import { Box3, Object3D, Vector2, Vector3 } from "three";
import { DecorItemI } from "../../../../shared";

export type OrientedFootprint = {
    center: Vector2;
    halfSize: Vector2;
    axisX: Vector2;
    axisZ: Vector2;
};

export type OrientedFootprintTemplate = {
    localCenter: Vector2;
    halfSize: Vector2;
};

const SAT_EPSILON = 0.00001;

export const isBicycleDecor = (
    decor: Pick<DecorItemI, "name" | "code" | "surface">
): boolean => {
    if (decor.surface !== "floor") return false;

    const name = (decor.name ?? "").trim().toLowerCase();
    const code = (decor.code ?? "").trim().toLowerCase();

    return (
        name === "велосипед" ||
        code === "velosiped" ||
        code === "bicycle" ||
        code === "bike"
    );
};

export const createAabbFootprint = (box: Box3): OrientedFootprint => {
    const center3d = box.getCenter(new Vector3());
    const size3d = box.getSize(new Vector3());

    return {
        center: new Vector2(center3d.x, center3d.z),
        halfSize: new Vector2(size3d.x / 2, size3d.z / 2),
        axisX: new Vector2(1, 0),
        axisZ: new Vector2(0, 1),
    };
};

export const createOrientedFootprintTemplate = (
    object: Object3D,
    marginMm = 0
): OrientedFootprintTemplate => {
    const normalized = object.clone(true);

    normalized.position.set(0, 0, 0);

    normalized.rotation.y = 0;
    normalized.updateMatrixWorld(true);

    const box = new Box3().setFromObject(normalized);
    const center = box.getCenter(new Vector3());
    const size = box.getSize(new Vector3());
    const margin = Math.max(0, marginMm) / 1000;

    return {
        localCenter: new Vector2(center.x, center.z),
        halfSize: new Vector2(size.x / 2 + margin, size.z / 2 + margin),
    };
};

export const createOrientedFootprintFromTemplate = (
    template: OrientedFootprintTemplate,
    position: Vector3,
    rotationY: number
): OrientedFootprint => {
    const cos = Math.cos(rotationY);
    const sin = Math.sin(rotationY);

    const localX = template.localCenter.x;
    const localZ = template.localCenter.y;

    return {
        center: new Vector2(
            position.x + cos * localX + sin * localZ,
            position.z - sin * localX + cos * localZ
        ),
        halfSize: template.halfSize.clone(),
        axisX: new Vector2(cos, -sin),
        axisZ: new Vector2(sin, cos),
    };
};

const getProjectionRadius = (footprint: OrientedFootprint, axis: Vector2): number => {
    return (
        footprint.halfSize.x * Math.abs(footprint.axisX.dot(axis)) +
        footprint.halfSize.y * Math.abs(footprint.axisZ.dot(axis))
    );
};

export const intersectsOrientedFootprints = (
    first: OrientedFootprint,
    second: OrientedFootprint
): boolean => {
    const delta = second.center.clone().sub(first.center);
    const axes = [first.axisX, first.axisZ, second.axisX, second.axisZ];

    for (const axis of axes) {
        const distance = Math.abs(delta.dot(axis));
        const firstRadius = getProjectionRadius(first, axis);
        const secondRadius = getProjectionRadius(second, axis);

        if (distance >= firstRadius + secondRadius - SAT_EPSILON) {
            return false;
        }
    }

    return true;
};
